// THE OPERATIONS INBOX'S TASK TYPES: one code list, and the only place a kind's
// workstream, cadence, defaults, subject and archive rule are written down.
//
// A card's `kind` column holds a key from this list. The settings table
// (`ops_kind_setting`) stores overrides of the defaults here and nothing else,
// so a kind nobody has touched in settings runs exactly as written below, and a
// kind dropped from this list leaves rows that name a key the code no longer
// knows (the readers skip them; nothing deletes them).
//
// Shared, not server-only: the page draws the labels, and the tick (a sidecar,
// under node's type stripping) reads the rest. Erasable TypeScript only.

import { operationsDay } from './throughput-operations';

export const WORKSTREAMS = [
  { key: 'creator-scripts', label: 'Creator Scripts' },
  { key: 'affiliate-campaigns', label: 'Affiliate Campaigns' },
  { key: 'weekly-reports', label: 'Weekly Reports' },
  { key: 'client-onboarding', label: 'Client Onboarding' },
  { key: 'client-comms', label: 'Client Comms' },
] as const;
export type Workstream = (typeof WORKSTREAMS)[number]['key'];

export const isWorkstream = (v: unknown): v is Workstream =>
  typeof v === 'string' && WORKSTREAMS.some((w) => w.key === v);

/** How often a kind's card comes due. `event` kinds appear when their event happens; `ad-hoc` is typed in. */
export type Cadence = 'daily' | 'weekly' | 'monthly' | 'event' | 'ad-hoc';
export const CADENCES: readonly Cadence[] = ['daily', 'weekly', 'monthly', 'event', 'ad-hoc'];
export const isCadence = (v: unknown): v is Cadence => typeof v === 'string' && (CADENCES as readonly string[]).includes(v);

/**
 * WHAT A CARD IS ABOUT, which is also what makes it the same card tomorrow.
 * A card is reused for its subject and never remade, so the subject carries the
 * period wherever a kind has one: a weekly kind's subject names its week, and
 * next week is a new card; a daily counting kind's names only the brand, and the
 * same card takes each new day.
 */
export type SubjectShape =
  | 'brand'
  | 'brand-week'
  | 'creator-brand-week'
  | 'creator-brand'
  | 'brand-section'
  | 'brand-item'
  | 'card';

/**
 * WHAT ARCHIVE MEANS FOR THE KIND. Archive hides a card until its work changes:
 *   period     the subject has a period: it stays archived, and next period is a new card
 *   next-day   a daily counting card: back at the next day's roll, if work is owed then
 *   newer-work back when the kind's reader finds work that began after the archive
 *   by-hand    an ad-hoc card: only unarchiving brings it back
 */
export type ArchiveRule = 'period' | 'next-day' | 'newer-work' | 'by-hand';

export interface KindDef {
  key: string;
  label: string;
  /** Null only for ad hoc, whose workstream is the person's pick (which may be none). */
  workstream: Workstream | null;
  /** The cadence the code runs it on, unless `ops_kind_setting.cadence` names another of `cadences`. */
  cadence: Cadence;
  /**
   * The cadences the tick can run the kind on; absent, its `cadence` alone. A
   * setting naming any other is ignored (the settings page shows the one in
   * force), since a kind's subject and due day are built for these.
   */
  cadences?: readonly Cadence[];
  /** Draws a counter, a done count of a target (12/37). Only the daily kinds freeze the target, at the first action of the day. */
  counting: boolean;
  /** Whether the card can be late. A daily counting card carries over to the next day instead. */
  goesLate: boolean;
  subject: SubjectShape;
  archive: ArchiveRule;
  /** For the archive rule `newer-work`: what counts as new work, in the reader's terms. */
  newWork?: string;
  /** The defaults `ops_kind_setting` overrides. */
  defaults: {
    enabled: boolean;
    /** 0 Sunday … 6 Saturday, for a weekly kind due on one day of its week. */
    dueWeekday?: number;
    /** Complete an action item: made this many days after the day its status was confirmed. */
    firstAfterDays?: number;
    /** Again every this many days while it is open: no kind uses it now; the column stays. */
    repeatDays?: number;
  };
}

export const OPS_KINDS = [
  {
    key: 'review-sample-requests', label: 'Review sample requests', workstream: 'affiliate-campaigns',
    cadence: 'daily', counting: true, goesLate: false, subject: 'brand', archive: 'next-day',
    defaults: { enabled: true },
  },
  {
    key: 'review-messages', label: 'Review messages', workstream: 'affiliate-campaigns',
    cadence: 'daily', counting: true, goesLate: false, subject: 'brand', archive: 'next-day',
    defaults: { enabled: true },
  },
  {
    key: 'launch-campaign', label: 'Launch a campaign', workstream: 'affiliate-campaigns',
    // Weekly from Sunday, or monthly from the 1st (the setting's cadence): the subject's period is its first day.
    cadence: 'weekly', cadences: ['weekly', 'monthly'], counting: false, goesLate: true, subject: 'brand-week', archive: 'period',
    defaults: { enabled: true, dueWeekday: 3 },
  },
  {
    // Per creator, brand and week: a card sits on one brand's pod seat, and a
    // creator writing for two pods' brands in one week owes two reviewers. Its
    // counter is the week's scripts reviewed of those sent for review; no freeze.
    key: 'review-scripts', label: 'Review scripts', workstream: 'creator-scripts',
    cadence: 'event', counting: true, goesLate: true, subject: 'creator-brand-week', archive: 'period',
    defaults: { enabled: true },
  },
  {
    key: 'overdue-scripts', label: 'Overdue scripts', workstream: 'creator-scripts',
    cadence: 'event', counting: false, goesLate: true, subject: 'creator-brand', archive: 'newer-work',
    newWork: 'the creator has a late week that was not late when the card was archived',
    defaults: { enabled: true },
  },
  {
    key: 'weekly-report', label: 'Complete the weekly report', workstream: 'weekly-reports',
    cadence: 'weekly', counting: false, goesLate: true, subject: 'brand-week', archive: 'period',
    defaults: { enabled: true, dueWeekday: 1 },
  },
  {
    key: 'onboarding-section', label: 'Onboarding section', workstream: 'client-onboarding',
    cadence: 'event', counting: true, goesLate: true, subject: 'brand-section', archive: 'newer-work',
    newWork: 'an item in the section became available after the card was archived',
    defaults: { enabled: true },
  },
  {
    key: 'client-messages', label: 'Unanswered client messages', workstream: 'client-comms',
    cadence: 'event', counting: true, goesLate: true, subject: 'brand', archive: 'newer-work',
    newWork: 'a wait opened after the card was archived',
    defaults: { enabled: true },
  },
  {
    // Per brand, while any of its items waits to be confirmed (the Action Items page's "to confirm" over All).
    // Its counter is the day's work, drawn as what is left ("57 to review"), so it draws no x/y.
    key: 'review-action-items', label: 'Review action items', workstream: 'client-comms',
    cadence: 'event', counting: false, goesLate: true, subject: 'brand', archive: 'newer-work',
    newWork: 'an item raised after the card was archived waits to be confirmed',
    defaults: { enabled: true },
  },
  {
    // Per action item we owe, from the day after its status was confirmed while it is open work with the ball
    // ours: the one an AM Ops person snoozes, or finishes by finishing the item. The client's ball closes it.
    key: 'complete-action-item', label: 'Complete an action item', workstream: 'client-comms',
    cadence: 'event', counting: false, goesLate: true, subject: 'brand-item', archive: 'newer-work',
    newWork: 'the item was confirmed again after the card was archived',
    defaults: { enabled: true, firstAfterDays: 1 },
  },
  {
    key: 'ad-hoc', label: 'Task', workstream: null,
    cadence: 'ad-hoc', counting: false, goesLate: true, subject: 'card', archive: 'by-hand',
    defaults: { enabled: true },
  },
] as const satisfies readonly KindDef[];

export type OpsKind = (typeof OPS_KINDS)[number]['key'];

export const isOpsKind = (v: unknown): v is OpsKind => typeof v === 'string' && OPS_KINDS.some((k) => k.key === v);

/**
 * THE CADENCE A KIND RUNS ON: a stored one when the kind can run on it, else
 * the code's. Null for a key the code no longer knows.
 */
export function cadenceOf(kind: string, stored: unknown): Cadence | null {
  const def = kindDef(kind);
  if (def === null) return null;
  return isCadence(stored) && (def.cadences ?? [def.cadence]).includes(stored) ? stored : def.cadence;
}

/** The kind's definition, or null for a key the code no longer knows. */
export function kindDef(key: string): KindDef | null {
  return (OPS_KINDS as readonly KindDef[]).find((k) => k.key === key) ?? null;
}

export interface SubjectParts {
  brand?: string;
  /** A week's first day, YYYY-MM-DD. */
  week?: string;
  /** The creator's email, as the scripts key them. */
  creator?: string;
  section?: string;
  /** An action item's id. */
  item?: string;
  cardId?: string;
}

/**
 * THE SUBJECT A CARD IS STORED UNDER, unique with its kind. Parts are joined
 * with `::`, the scripts' week-key separator, so a creator's address inside one
 * is rewritten by the schema sample's `ref` rule like every other key. A missing
 * part throws: a subject built from an undefined is a second card for the same
 * work.
 */
export function subjectFor(kind: string, p: SubjectParts): string {
  const def = kindDef(kind);
  if (!def) throw new Error(`unknown ops inbox kind "${kind}"`);
  const need = (name: keyof SubjectParts): string => {
    const v = p[name]?.trim();
    if (!v) throw new Error(`${kind}: a subject needs ${name}`);
    if (v.includes('::')) throw new Error(`${kind}: ${name} cannot hold "::"`);
    return name === 'creator' ? v.toLowerCase() : v;
  };
  switch (def.subject) {
    case 'brand': return need('brand');
    case 'brand-week': return `${need('brand')}::${need('week')}`;
    case 'creator-brand-week': return `${need('creator')}::${need('brand')}::${need('week')}`;
    case 'creator-brand': return `${need('creator')}::${need('brand')}`;
    case 'brand-section': return `${need('brand')}::${need('section')}`;
    case 'brand-item': return `${need('brand')}::${need('item')}`;
    case 'card': return need('cardId');
  }
}

/**
 * WHETHER AN ARCHIVED CARD COMES BACK, by its kind's rule. `newestWorkAt` is
 * when the work the reader finds owed now began (ms), or null when nothing is
 * owed; a card with nothing owed never comes back, whatever its rule.
 */
export function comesBackFromArchive(
  kind: string,
  card: { archivedAt: number },
  now: { at: number; newestWorkAt: number | null },
): boolean {
  const def = kindDef(kind);
  if (!def || now.newestWorkAt === null) return false;
  switch (def.archive) {
    case 'period':
    case 'by-hand':
      return false;
    case 'next-day':
      return operationsDay(now.at) > operationsDay(card.archivedAt);
    case 'newer-work':
      return now.newestWorkAt > card.archivedAt;
  }
}
